import {
  CameraHelper,
  DirectionalLight,
  DirectionalLightHelper,
  FloatType,
  Group,
  HalfFloatType,
  Mesh,
  MeshPhongMaterial,
  OrthographicCamera,
  PlaneGeometry,
  Scene,
  ShaderMaterial,
  Vector3,
  WebGLRenderTarget,
} from 'three';

import { seededRandom } from 'three/src/math/MathUtils';
import { appMixin } from '../utils/appMixin';

const SAMPLES = 100;
const RADIUS = 3;
const AO_WEIGHT = 0.5;
// const AO_WEIGHT = 1;

export class StaticLightComputing extends appMixin() {
  constructor(app) {
    super(app);
    this.scene = new Scene();
    this.origin = new Vector3(2.5, 4.5, 4.5);
    this.directional = new DirectionalLight(0xffffff, 0.7);

    this.t = 0;

    this.directional.castShadow = true;
    this.directional.shadow.camera.near = 2;
    this.directional.shadow.camera.far = 10;
    this.directional.shadow.camera.right = 3;
    this.directional.shadow.camera.left = -3;
    this.directional.shadow.camera.top = 3;
    this.directional.shadow.camera.bottom = -3;
    this.directional.shadow.mapSize.width = 1024;
    this.directional.shadow.mapSize.height = 1024;
    this.directional.shadow.bias = -0.001;
    this.directional.shadow.radius = 2;

    this.camera = new OrthographicCamera(-2, 2, -2, -2, 0, 20);

    this.scene.add(this.directional);
  }

  compute = (model) => {
    this.uniforms = {
      uFlatten: { value: 1 },
      uErase: { value: 0 },
      tLastMap: { value: null },
      uTime: { value: 0 },
    };

    this.renderTarget1?.dispose();
    this.renderTarget2?.dispose();
    const format = /(Android|iPad|iPhone|iPod)/g.test(navigator.userAgent)
      ? HalfFloatType
      : FloatType;
    this.renderTarget1 = new WebGLRenderTarget(1024, 1024, { type: format });
    this.renderTarget2 = new WebGLRenderTarget(1024, 1024, { type: format });

    this.renderer.shadowMap.enabled = true;
    // this.renderer.shadowMap.type = VSMShadowMap;
    this.renderer.shadowMap.autoUpdate = false;
    this.scene.remove(this.model);

    // this.uniforms.tLastMap.value = null;
    if (this.blur) this.blur.visible = false;
    if (this.model) this.model.visible = false;

    this.uniforms.uErase.value = 0;

    this.uniforms.tLastMap.value = null;
    if (this.blur) this.blur.material.uniforms.tMap.value = null;
    this.renderer.setRenderTarget(this.renderTarget1);
    this.renderer.render(this.scene, this.camera);

    this.renderer.setRenderTarget(this.renderTarget2);
    this.renderer.render(this.scene, this.camera);

    this.uniforms.uErase.value = 1;

    if (this.blur) this.blur.visible = true;
    if (this.model) this.model.visible = true;

    this.model = model.clone();

    this.model.scale.set(0.021, 0.021, 0.021);
    // this.model.position.y = -5;

    this.material = new MeshPhongMaterial({
      color: 0xffffff,
      side: 2,
      flatShading: false,
      dithering: true,
    });

    this.material.onBeforeCompile = (shader) => {
      shader.uniforms = { ...shader.uniforms, ...this.uniforms };

      shader.vertexShader = shader.vertexShader.replace(
        `varying vec3 vViewPosition;`,
        `varying vec3 vViewPosition;
        
        uniform float uFlatten;
        uniform float uTime;

        attribute vec2 uv2;
        varying vec2 vUv2;
        `
      );

      shader.vertexShader = shader.vertexShader.replace(
        `#include <fog_vertex>`,
        `#include <fog_vertex>
            vUv2 = uv2;
            gl_Position = vec4((uv2 - 0.5) * 2.0, 1.0, 1.0); 
        `
      );

      shader.fragmentShader = shader.fragmentShader.replace(
        `uniform float opacity;`,
        `uniform float opacity;
        uniform sampler2D tLastMap;
        uniform float uTime;
        uniform float uErase;
        varying vec2 vUv2;
        `
      );

      shader.fragmentShader = shader.fragmentShader.replace(
        `#include <normal_fragment_begin>`,
        `float faceDirection = 1.;//gl_FrontFacing ? 1.0 : - 1.0;

        #ifdef FLAT_SHADED
        
          vec3 fdx = dFdx( vViewPosition );
          vec3 fdy = dFdy( vViewPosition );
          vec3 normal = normalize( cross( fdx, fdy ) );
        
        #else
        
          vec3 normal = normalize( vNormal );
        
          #ifdef DOUBLE_SIDED
        
            normal = normal * faceDirection;
        
          #endif
        
          #ifdef USE_TANGENT
        
            vec3 tangent = normalize( vTangent );
            vec3 bitangent = normalize( vBitangent );
        
            #ifdef DOUBLE_SIDED
        
              tangent = tangent * faceDirection;
              bitangent = bitangent * faceDirection;
        
            #endif
        
            #if defined( TANGENTSPACE_NORMALMAP ) || defined( USE_CLEARCOAT_NORMALMAP )
        
              mat3 vTBN = mat3( tangent, bitangent, normal );
        
            #endif
        
          #endif
        
        #endif
        
        // non perturbed normal for clearcoat among others
        
        vec3 geometryNormal = normal;
        `
      );

      shader.fragmentShader = shader.fragmentShader.replace(
        `#include <dithering_fragment>`,
        `#include <dithering_fragment>
        vec4 last = texture2D(tLastMap, vUv2);

        if(uErase > .5) gl_FragColor = mix(last, gl_FragColor, 0.01);
        `
      );
    };

    let n = 0;
    this.model.traverse((o) => {
      if (o.isMesh) {
        o.geometry.computeVertexNormals();
        const material = this.material.clone();
        material.map = o.material.uniforms.tMap.value;
        material.onBeforeCompile = this.material.onBeforeCompile;
        o.material = this.material;
        o.frustumCulled = false;
        o.castShadow = true;
        o.receiveShadow = true;
        o.renderOrder = 1000 + n++;
      }
    });

    this.scene.add(this.model);

    if (typeof this.blur === 'undefined') this.addBlur();

    for (let i = 0; i < SAMPLES; i++) this.render();

    return { texture: this.renderTarget1.texture };
  };

  addBlur() {
    const geometry = new PlaneGeometry(2, 2);
    const material = new ShaderMaterial({
      vertexShader: `
      varying vec2 vUv;
      
      void main(){
        vUv = uv;
        gl_Position = vec4(position,1.);
      }
      `,
      fragmentShader: `
      uniform sampler2D tMap;
      varying vec2 vUv;
      uniform float uTime;
      
      void main(){
        vec2 uv = vUv;
        float decay = 1. / 1024.;
        vec4 last = vec4(0.);
        last += texture2D(tMap, vUv + vec2(decay, 0.));
        last += texture2D(tMap, vUv + vec2(-decay, 0.));
        last += texture2D(tMap, vUv + vec2(0., decay));
        last += texture2D(tMap, vUv + vec2(0., -decay));
        last += texture2D(tMap, vUv + vec2(decay, decay));
        last += texture2D(tMap, vUv + vec2(decay, -decay));
        last += texture2D(tMap, vUv + vec2(-decay, decay));
        last += texture2D(tMap, vUv + vec2(-decay, -decay));
        last /= 8.;
        last = mix(texture2D(tMap, vUv), last, .5);

        gl_FragColor = last;
      }
      `,

      uniforms: {
        tMap: { value: this.renderTarget2.texture },
        uTime: this.app.uniforms.uTime,
      },
      // blending: AdditiveBlending,
      depthWrite: false,
    });

    this.blur = new Mesh(geometry, material);
    this.blur.renderOrder = 0;
    this.blur.frustumCulled = false;
    // this.blur.visible = false;
    this.scene.add(this.blur);
  }

  render() {
    this.renderer.shadowMap.needsUpdate = true;
    if (seededRandom(this.t * 1 + 1) > AO_WEIGHT) {
      this.directional.position.set(
        this.origin.x + (seededRandom(this.t * 2 + 2) - 0.5) * RADIUS,
        this.origin.y + (seededRandom(this.t * 3 + 3) - 0.5) * RADIUS,
        this.origin.z + (seededRandom(this.t * 4 + 4) - 0.5) * RADIUS
      );
    } else {
      const lambda = Math.acos(2 * seededRandom(this.t * 5 + 5) - 1) - Math.PI / 2.0;
      const phi = Math.PI * seededRandom(this.t * 6 + 6) * 2;
      this.directional.position.set(
        Math.cos(lambda) * Math.cos(phi) * 4,
        Math.cos(lambda) * Math.sin(phi) * 4 + 0.5,
        Math.sin(lambda) * 4
      );
      // this.directional.position.set((Math.random() - 0.5) * 5, (Math.random() - 0.5) * 5, (Math.random() - 0.5) * 5);
    }

    this.directional.target.position.set(-0.2, 1.6, 0.3);
    this.directional.target.needsUpdate = true;
    this.directional.target.updateMatrixWorld();
    this.directional.updateMatrixWorld();

    // const debug = new DirectionalLightHelper(this.directional.clone(), 0.2);
    // this.app.webgl.scene.add(debug);

    // const debug = new CameraHelper(this.directional.shadow.camera.clone());
    // this.app.webgl.scene.add(debug);

    this.uniforms.uTime.value = this.t;
    if (this.t++ % 2 === 0) {
      this.uniforms.tLastMap.value = this.renderTarget2.texture;
      if (this.blur) this.blur.material.uniforms.tMap.value = this.renderTarget2.texture;
      this.renderer.setRenderTarget(this.renderTarget1);
      this.renderer.render(this.scene, this.camera);
    } else {
      this.uniforms.tLastMap.value = this.renderTarget1.texture;
      if (this.blur) this.blur.material.uniforms.tMap.value = this.renderTarget1.texture;
      this.renderer.setRenderTarget(this.renderTarget2);
      this.renderer.render(this.scene, this.camera);
    }
  }

  onRender = () => {
    if (this.model) {
      // this.render();
      // this.renderer.setRenderTarget(null);
      // this.renderer.render(this.scene, app.webgl.camera);
    }
  };

  get renderer() {
    return this.app.webgl.renderer;
  }
}
